import {Action} from "redux-actions";
import {OnFailure, OnSuccess, OnSuccessData} from "../actionHelpers";
import {api} from "../../../api/api";
import {Dispatch} from "react-redux";
import LeadStatus from "../../../model/lead/LeadStatus";
import {
	LEAD_STATUS_CONFIG_EDITED,
	LEAD_STATUS_CONFIG_FETCHED,
	LEAD_STATUS_EDITED,
	LEAD_STATUS_SORT_ORDER_UPDATED,
	LEAD_STATUSES_FETCHED
} from "./actionType";
import LeadStatusSettings from "../../../model/lead/LeadStatusSetting";

/**
 * Lead statuses fetched successful action.
 * @return {Action<LEAD_STATUSES_FETCHED>}
 * @param leadStatuses
 */
function leadStatusesFetched(leadStatuses: Array<LeadStatus>): Action<LEAD_STATUSES_FETCHED> {
	return {type: LEAD_STATUSES_FETCHED, payload: {leadStatuses}};
}

/**
 * Update lead statuses sort order successful action.
 * @return {Action<LEAD_STATUS_SORT_ORDER_UPDATED>}
 * @param leadStatuses
 */
function leadStatusesSortOrderUpdated(leadStatuses: Array<LeadStatus>): Action<LEAD_STATUS_SORT_ORDER_UPDATED> {
	return {type: LEAD_STATUS_SORT_ORDER_UPDATED, payload: {leadStatuses}};
}

/**
 * Edit lead status successful action.
 * @return {Action<LEAD_STATUS_EDITED>}
 * @param leadStatus
 */
function leadStatusEdited(leadStatus: LeadStatus): Action<LEAD_STATUS_EDITED> {
	return {type: LEAD_STATUS_EDITED, payload: {leadStatus: leadStatus}};
}

/**
 * Lead status config fetched successful action.
 * @return {Action<LEAD_STATUS_CONFIG_FETCHED>}
 * @param leadStatusConfig
 */
function leadStatusConfigFetched(leadStatusConfig: Array<LeadStatusSettings>): Action<LEAD_STATUS_CONFIG_FETCHED> {
	return {type: LEAD_STATUS_CONFIG_FETCHED, payload: {leadStatusConfig}};
}

/**
 * Edit lead status config successful action.
 * @return {Action<LEAD_STATUS_CONFIG_EDITED>}
 * @param leadStatusConfig
 */
function leadStatusConfigEdited(leadStatusConfig: LeadStatusSettings): Action<LEAD_STATUS_CONFIG_EDITED> {
	return {type: LEAD_STATUS_CONFIG_EDITED, payload: {leadStatusConfig}};
}

/**
 * Fetch lead statuses from server.
 * @param {OnSuccessData} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchLeadStatuses(onSuccess: OnSuccessData<LeadStatus[]>, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadStatusApi.fetchLeadStatuses().then(leadStatuses => {
			dispatch(leadStatusesFetched(leadStatuses));
			onSuccess(leadStatuses);
		}).catch(onFailure);
	};
}

/**
 * Update lead status sort order.
 * @param statuses
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateLeadStatusSortOrder(statuses: LeadStatus[],
	onSuccess: OnSuccess,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadStatusApi.updateSortOrder(statuses).then(() => {
			dispatch(leadStatusesSortOrderUpdated(statuses));
			dispatch(onSuccess);
		}).catch(onFailure);
	}
}

/**
 * Edit lead status action.
 * @param leadStatusId
 * @param data
 * @param {OnSuccessData<LeadStatus>} onSuccess Success callback.
 * @param {OnFailure} onFailure Failure callback.
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function editLeadStatus(leadStatusId: string,
	data: LeadStatus,
	onSuccess: OnSuccessData<LeadStatus>,
	onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadStatusApi.editLeadStatus(leadStatusId, data).then((leadStatus) => {
			dispatch(leadStatusEdited(leadStatus));
			onSuccess(leadStatus);
		}).catch(onFailure);
	}
}

/**
 * Fetch lead status config from server.
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function fetchLeadStatusConfig(onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadStatusApi.fetchLeadStatusConfig().then(leadStatusConfig => {
			dispatch(leadStatusConfigFetched(leadStatusConfig));
			onSuccess();
		}).catch(onFailure);
	};
}

/**
 * Update lead status config
 * @param {LeadStatusSettings } lss
 * @param {OnSuccess} onSuccess
 * @param {OnFailure} onFailure
 * @return {(dispatch: Dispatch<any>) => void}
 */
export function updateLeadStatusConfig(lss: LeadStatusSettings, onSuccess: OnSuccess, onFailure: OnFailure) {
	return function(dispatch: Dispatch<any>) {
		api.instance.leadStatusApi.updateLeadStatusConfig(lss.toJson()).then(() => {
			dispatch(leadStatusConfigEdited(lss));
			onSuccess();
		}).catch(onFailure);
	};
}